import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { useTerritorio } from "../../context/TerritorioContext";
import { getVisitasBac, getPaiVacunacion } from "../api/bacSarampionAPI.js";
import SelectorJornada, { coincideJornada } from "../jornadas/SelectorJornada";
import { etiquetaJornada } from "../jornadas/jornadasBac";

// Fix del ícono default de Leaflet con bundlers (Vite/CRA) — sin esto
// los marcadores por defecto salen rotos. Estándar, no específico de este módulo.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ── Capa "Visitas BAC": color por estado de la vivienda ─────────────────
const COLOR_ESTADO = {
  Efectiva:     "#2d6a4f",
  "No efectiva": "#a15c9e",
  Cerrada:      "#b7791f",
  Renuente:     "#b3261e",
  "No habitada": "#8b95a1",
};

// ── Mapa "Barrido PAI": un filtro a la vez, contado POR PERSONA ────────
// Cada punto es una vivienda y su número = personas de esa vivienda que
// cumplen el filtro elegido. Solo se dibujan las viviendas con al menos una.
//   cuenta(r):   cuántas personas de la vivienda cumplen el filtro
//   personas(r): (si existe) quiénes son — solo para lo que se registra
//                persona por persona (el carnet); lo demás viene como conteo.
const sinCarnetDe = (r) => (r.carnets || []).filter((c) => c.presenta_evidencia !== "Si");
const conCarnetDe = (r) => (r.carnets || []).filter((c) => c.presenta_evidencia === "Si");
const FILTROS_PAI = [
  { id: "encontradas", label: "👥 Personas encontradas",      corto: "encontradas",               color: "#16342a", cuenta: (r) => r.encontrada_total || 0 },
  { id: "sin_carnet",  label: "🪪 No presenta carnet",         corto: "sin carnet",                color: "#e07b00", cuenta: (r) => sinCarnetDe(r).length, personas: sinCarnetDe },
  { id: "con_carnet",  label: "✅ Con carnet o PAIWEB",        corto: "con carnet o PAIWEB",       color: "#2d6a4f", cuenta: (r) => conCarnetDe(r).length, personas: conCarnetDe },
  { id: "vac_previa",  label: "🛡 Vacunadas previamente",      corto: "vacunadas previamente",     color: "#5b7f2a", cuenta: (r) => r.vacunada_previa_total || 0 },
  { id: "vacunadas",   label: "💉 Vacunadas en el barrido",    corto: "vacunadas en el barrido",   color: "#0C447C", cuenta: (r) => r.vacunada_barrido_total || 0 },
  { id: "no_vacunadas", label: "🚫 No vacunadas en el barrido", corto: "no vacunadas en el barrido", color: "#b3261e", cuenta: (r) => r.no_vacunada_barrido_total || 0 },
];

// Filtros del mapa de visitas: se arman con lo que trae la data — estado
// (Efectiva / No efectiva) y cada motivo de "No efectiva" que aparezca
// (Cerrada, Renuente, Menor en casa…), más las revisitas pendientes.
function filtrosVisitas(base) {
  const estados = [...new Set(base.map((p) => p.cat).filter(Boolean))];
  const motivos = [...new Set(base.map((p) => p.r.motivo_no_efectiva).filter(Boolean))].sort();
  const icono = { Efectiva: "✅", "No efectiva": "❌", Cerrada: "🔒", Renuente: "✋", "No habitada": "🏚", "Local Comercial": "🏪" };
  return [
    { id: "todos", label: "Todas", pred: () => true },
    ...["Efectiva", "No efectiva", ...estados.filter((e) => !["Efectiva", "No efectiva"].includes(e))]
      .filter((e, i, a) => a.indexOf(e) === i && (estados.includes(e) || e === "Efectiva" || e === "No efectiva"))
      .map((e) => ({ id: `estado:${e}`, label: `${icono[e] || "•"} ${e}`, pred: (p) => p.cat === e, color: COLOR_ESTADO[e] })),
    ...motivos.map((m) => ({ id: `motivo:${m}`, label: `${icono[m] || "↳"} ${m}`, pred: (p) => p.r.motivo_no_efectiva === m, color: COLOR_ESTADO[m] || "#a15c9e" })),
    { id: "revisita", label: "🔁 Revisita pendiente", pred: (p) => !!p.r.fecha_revisita_sugerida, color: "#b7791f" },
  ];
}

const esc = (t) => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const p2 = (n) => String(n).padStart(2, "0");
// Fecha LOCAL (no UTC) del registro, para filtrar y mostrar igual que en el celular
const fechaLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};
const horaLocal = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : `${p2(d.getHours())}:${p2(d.getMinutes())}`; };
const fmtFecha = (ymd) => { if (!ymd) return ""; const [y, m, d] = ymd.split("-"); return `${d}/${m}/${y}`; };

/**
 * focoCodigoVivienda: si viene con un código (botón "📍" de una fila),
 * el mapa se centra en ese punto y abre su popup apenas carga.
 * capaInicial: "visitas" | "pai" — con qué mapa abrir (el 📍 de la tabla
 * de barrido PAI abre directamente el mapa PAI).
 * onFocoConsumido: se llama una vez hecho el enfoque.
 */
export default function MapaBacSarampion({ focoCodigoVivienda, capaInicial = "visitas", onFocoConsumido, jornadas = [], filtroJornada = "", onFiltroJornada }) {
  const { data: ctx } = useTerritorio();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);
  const heatLayer = useRef(null);
  const marcadoresPorCodigo = useRef(new Map());

  const [capa, setCapa] = useState(capaInicial === "pai" ? "pai" : "visitas");
  const [visitas, setVisitas] = useState([]);
  const [pai, setPai] = useState([]);
  const [mostrarCalor, setMostrarCalor] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [alcance, setAlcance] = useState("comunidad"); // "comunidad" | "general"
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const filtroInicial = (c) => (c === "pai" ? "encontradas" : "todos");
  const [filtro, setFiltro] = useState(filtroInicial(capaInicial === "pai" ? "pai" : "visitas"));
  const primeraCapa = useRef(true);
  useEffect(() => { if (primeraCapa.current) { primeraCapa.current = false; return; } setFiltro(filtroInicial(capa)); }, [capa]);
  const [descargando, setDescargando] = useState(false);

  // Si llegaste desde el 📍 de una fila, el mapa muestra SOLO ese registro
  // hasta que el usuario pida ver todos los puntos.
  const [codigoFoco] = useState(focoCodigoVivienda || null);
  const [soloEsteRegistro, setSoloEsteRegistro] = useState(!!focoCodigoVivienda);

  // ── Cargar datos de la capa activa ──────────────────────────────────
  useEffect(() => {
    if (!ctx?.territorio) return;
    setCargando(true);
    const filtro = alcance === "general"
      ? { territorio: ctx.territorio }
      : { territorio: ctx.territorio, comunidad: ctx.comunidad };
    const peticion = capa === "pai"
      ? Promise.all([getPaiVacunacion(filtro), getVisitasBac(filtro).catch(() => ({ data: [] }))]).then(([resPai, resVis]) => {
          // Coordenadas: las trae el registro PAI (servidor actualizado) o,
          // si no, se toman de la visita BAC de la misma vivienda.
          const coords = new Map();
          for (const v of resVis.data || []) if (v.lat && v.lng && !coords.has(v.codigo_vivienda)) coords.set(v.codigo_vivienda, { lat: v.lat, lng: v.lng });
          // Se guardan TODOS los registros PAI con coordenadas; el más
          // reciente por vivienda se elige después de filtrar por jornada.
          const filas = [];
          for (const r of resPai.data || []) {
            const cod = r.visita?.codigo_vivienda;
            if (!cod) continue;
            const c = r.visita?.lat && r.visita?.lng ? { lat: r.visita.lat, lng: r.visita.lng } : coords.get(cod);
            if (!c) continue;
            filas.push({ ...r, visita: { ...r.visita, ...c } });
          }
          setPai(filas);
        })
      : getVisitasBac(filtro).then((res) => setVisitas((res.data || []).filter((v) => v.lat && v.lng)));
    peticion
      .catch(() => (capa === "pai" ? setPai([]) : setVisitas([])))
      .finally(() => setCargando(false));
  }, [ctx?.territorio, ctx?.comunidad, alcance, capa]);

  // ── Puntos de la capa activa, filtrados por fecha y foco (sin filtro de tipo) ──
  // La jornada solo aplica viendo la comunidad (las jornadas son por comunidad)
  const fj = alcance === "comunidad" ? filtroJornada : "";
  const jornadaElegida = fj && fj !== "__sin__" ? jornadas.find((j) => j.codigo === fj) : null;

  const base = useMemo(() => {
    const enRango = (f) => (!desde || f >= desde) && (!hasta || f <= hasta);
    let lista;
    if (capa === "pai") {
      // Una vivienda puede tener varios registros PAI (revisitas): se deja
      // el más reciente DENTRO de la jornada elegida (vienen del más nuevo
      // al más viejo).
      const vistos = new Set();
      const paiJornada = pai.filter((r) => {
        if (!coincideJornada(r.visita?.jornada_codigo, fj)) return false;
        const cod = r.visita?.codigo_vivienda;
        if (vistos.has(cod)) return false;
        vistos.add(cod);
        return true;
      });
      lista = paiJornada.map((r) => {
        const v = r.visita || {};
        const fecha = fechaLocal(v.createdAt || r.createdAt);
        return {
          codigo: v.codigo_vivienda, lat: Number(v.lat), lng: Number(v.lng), fecha, r,
          cabecera: `
              <div style="font-family: monospace; font-size: 11px; color:#6c757d;">${esc(v.codigo_vivienda)}</div>
              <strong>${esc(v.comunidad)}, ${esc(v.territorio)}</strong>
              <div style="color:#6c757d;">📅 ${fmtFecha(fecha)} ${horaLocal(v.createdAt || r.createdAt)}${v.entrevistador ? ` · ${esc(v.entrevistador)}` : ""}</div>
              ${v.direccion ? `<div style="color:#6c757d;">${esc(v.direccion)}</div>` : ""}`,
        };
      });
    } else {
      // Un punto por vivienda: su intento más reciente (dentro de la jornada elegida)
      const ultimo = new Map();
      for (const v of visitas) {
        if (!coincideJornada(v.jornada_codigo, fj)) continue;
        const a = ultimo.get(v.codigo_vivienda);
        if (!a || (v.numero_visita || 0) >= (a.numero_visita || 0)) ultimo.set(v.codigo_vivienda, v);
      }
      lista = [...ultimo.values()].map((v) => {
        const fecha = fechaLocal(v.createdAt);
        return {
          codigo: v.codigo_vivienda, lat: Number(v.lat), lng: Number(v.lng), fecha, cat: v.estado_vivienda, r: v,
          color: COLOR_ESTADO[v.estado_vivienda] || "#8b95a1", punteado: !!v.fecha_revisita_sugerida,
          popup: `
            <div style="font-family: sans-serif; font-size: 13px;">
              <div style="font-family: monospace; font-size: 11px; color:#6c757d;">${esc(v.codigo_vivienda)}</div>
              <strong>${esc(v.comunidad)}, ${esc(v.territorio)}</strong>
              <div style="color:#6c757d;">📅 ${fmtFecha(fecha)} ${horaLocal(v.createdAt)}${v.entrevistador ? ` · ${esc(v.entrevistador)}` : ""}</div>
              <div style="margin-top:4px;">Estado: <b>${esc(v.estado_vivienda)}</b>${v.motivo_no_efectiva ? ` · ${esc(v.motivo_no_efectiva)}` : ""} ${v.numero_visita > 1 ? `(intento #${v.numero_visita})` : ""}</div>
              ${v.direccion ? `<div>${esc(v.direccion)}</div>` : ""}
              ${v.fecha_revisita_sugerida ? `<div style="color:#b7791f;">Revisita sugerida: ${esc(v.fecha_revisita_sugerida)}</div>` : ""}
            </div>`,
        };
      });
    }
    lista = lista.filter((p) => enRango(p.fecha));
    if (soloEsteRegistro && codigoFoco) lista = lista.filter((p) => p.codigo === codigoFoco);
    return lista;
  }, [capa, pai, visitas, desde, hasta, soloEsteRegistro, codigoFoco, fj]);

  // ── Filtro elegido ───────────────────────────────────────────────────
  // PAI: el número de cada botón = PERSONAS que cumplen el filtro.
  // Visitas: el número = viviendas en ese estado.
  const filtrosCapa = capa === "pai"
    ? FILTROS_PAI.map((f) => ({ ...f, n: base.reduce((a, p) => a + f.cuenta(p.r), 0) }))
    : filtrosVisitas(base).map((f) => ({ ...f, n: base.filter(f.pred).length }));
  const filtroActivo = filtrosCapa.find((f) => f.id === filtro) || filtrosCapa[0];
  const puntos = useMemo(() => {
    if (capa !== "pai") return base.filter(filtroActivo.pred);
    const f = filtroActivo;
    return base
      .map((p) => ({ ...p, n: f.cuenta(p.r) }))
      .filter((p) => p.n > 0)
      .map((p) => {
        const lista = f.personas ? f.personas(p.r) : null;
        return {
          ...p, color: f.color,
          popup: `
            <div style="font-family: sans-serif; font-size: 13px; min-width: 200px;">
              ${p.cabecera}
              <div style="margin-top:6px; font-weight:700; color:${f.color};">${p.n} ${p.n === 1 ? "persona" : "personas"} ${f.corto}</div>
              ${lista ? lista.map((c) => `<div>• ${esc(c.nombre)}${c.edad != null ? ` (${c.edad} ${esc((c.unidad_edad || "Años").toLowerCase())})` : ""}${c.numero_identificacion ? ` <span style="color:#6c757d;">· ${esc(c.numero_identificacion)}</span>` : ""}</div>`).join("") : ""}
            </div>`,
        };
      });
  }, [base, filtroActivo.id, capa]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inicializar el mapa una sola vez ────────────────────────────────
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;
    mapInstance.current = L.map(mapRef.current).setView([-4.2, -69.94], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
      crossOrigin: "anonymous", // necesario para poder descargar el mapa como imagen
    }).addTo(mapInstance.current);
    markersLayer.current = L.layerGroup().addTo(mapInstance.current);
  }, []);

  // ── Redibujar puntos ─────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance.current || !markersLayer.current) return;
    markersLayer.current.clearLayers();
    marcadoresPorCodigo.current.clear();
    const bounds = [];
    puntos.forEach((p) => {
      // Un punto por vivienda en su ubicación (sin números)
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: 8, color: "#0e2019",
        weight: p.punteado ? 2 : 1, dashArray: p.punteado ? "3,3" : null,
        fillColor: p.color, fillOpacity: 0.85,
      }).addTo(markersLayer.current);
      marker.bindPopup(p.popup);
      bounds.push([p.lat, p.lng]);
      marcadoresPorCodigo.current.set(p.codigo, { marker });
    });
    if (bounds.length) {
      mapInstance.current.fitBounds(bounds, { padding: [40, 40], maxZoom: soloEsteRegistro ? 17 : 15 });
    }
  }, [puntos]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Abrir el popup del punto enfocado apenas se dibuja ──────────────
  useEffect(() => {
    if (!codigoFoco || !mapInstance.current) return;
    const encontrado = marcadoresPorCodigo.current.get(codigoFoco);
    if (encontrado) encontrado.marker.openPopup();
    onFocoConsumido?.();
  }, [codigoFoco, puntos]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mapa de calor (toggle) ──────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance.current) return;
    if (heatLayer.current) { mapInstance.current.removeLayer(heatLayer.current); heatLayer.current = null; }
    if (mostrarCalor && puntos.length) {
      heatLayer.current = L.heatLayer(puntos.map((p) => [p.lat, p.lng, p.n != null ? Math.min(1, 0.25 + p.n * 0.15) : 0.6]), { radius: 35, blur: 25, maxZoom: 15 }).addTo(mapInstance.current);
      markersLayer.current.eachLayer((l) => mapInstance.current.removeLayer(l));
    } else {
      markersLayer.current?.addTo(mapInstance.current);
      markersLayer.current?.eachLayer((l) => l.addTo(mapInstance.current));
    }
  }, [mostrarCalor, puntos]);

  // ── Resumen por categoría (para la leyenda y la imagen descargada) ───
  const leyenda = capa === "pai"
    ? [{ label: `Personas ${filtroActivo.corto}`, color: filtroActivo.color, n: puntos.reduce((a, p) => a + p.n, 0) }]
    : Object.entries(COLOR_ESTADO).map(([label, color]) => ({ label, color, n: puntos.filter((p) => p.cat === label).length }))
        .filter((l) => l.n > 0 || ["Efectiva", "No efectiva"].includes(l.label));

  const resumen = capa === "pai"
    ? `${puntos.reduce((a, p) => a + p.n, 0)} personas ${filtroActivo.corto} en ${puntos.length} ${puntos.length === 1 ? "vivienda" : "viviendas"}`
    : `${puntos.length} viviendas georreferenciadas · ${puntos.filter((p) => !!p.r.fecha_revisita_sugerida).length} con revisita pendiente`;

  const textoRango = desde || hasta
    ? `${desde ? fmtFecha(desde) : "inicio"} a ${hasta ? fmtFecha(hasta) : "hoy"}`
    : "Todas las fechas";

  // ── Descargar el mapa como imagen PNG ────────────────────────────────
  // Se arma un canvas con: encabezado (título, lugar, fechas), los tiles
  // tal como se ven, el mapa de calor o los puntos, y la leyenda.
  const descargarMapa = async () => {
    const map = mapInstance.current, cont = mapRef.current;
    if (!map || !cont) return;
    setDescargando(true);
    map.closePopup();
    await new Promise((r) => setTimeout(r, 250));
    try {
      const rc = cont.getBoundingClientRect();
      const W = Math.round(rc.width), H = Math.round(rc.height);
      const escala = 2; // imagen nítida para imprimir o pegar en informes
      const CAB = 78;
      const filasLeyenda = Math.ceil(leyenda.length / 2);
      const PIE = 34 + filasLeyenda * 22;
      const cv = document.createElement("canvas");
      cv.width = W * escala; cv.height = (CAB + H + PIE) * escala;
      const g = cv.getContext("2d");
      g.scale(escala, escala);
      g.fillStyle = "#ffffff"; g.fillRect(0, 0, W, CAB + H + PIE);

      // Encabezado
      g.fillStyle = "#16342a"; g.fillRect(0, 0, W, CAB);
      g.fillStyle = "#ffffff"; g.font = "bold 18px Segoe UI, Arial, sans-serif";
      g.fillText(capa === "pai" ? "Mapa de barrido PAI — Sarampión/Rubéola" : "Mapa de visitas BAC — Sarampión", 16, 28);
      g.font = "13px Segoe UI, Arial, sans-serif";
      const lugar = alcance === "general" ? `${ctx?.territorio || ""} (todo el territorio)` : `${ctx?.territorio || ""} · ${ctx?.comunidad || ""}`;
      g.fillText(`📍 ${lugar}${jornadaElegida ? `   ·   Jornada ${etiquetaJornada(jornadaElegida).replace(/^\S+\s/, "")}` : fj === "__sin__" ? "   ·   Sin jornada" : ""}   ·   📅 ${textoRango}${filtroActivo.id !== "todos" ? `   ·   Filtro: ${filtroActivo.label}` : ""}`, 16, 50);
      g.fillStyle = "rgba(255,255,255,.8)"; g.font = "12px Segoe UI, Arial, sans-serif";
      const ahora = new Date();
      g.fillText(`${resumen}   ·   Generado ${fmtFecha(fechaLocal(ahora.toISOString()))} ${p2(ahora.getHours())}:${p2(ahora.getMinutes())}`, 16, 69);

      // Mapa: tiles (y capa de calor si está activa)
      g.save();
      g.beginPath(); g.rect(0, CAB, W, H); g.clip();
      g.fillStyle = "#e9ecef"; g.fillRect(0, CAB, W, H);
      cont.querySelectorAll(".leaflet-tile-pane img.leaflet-tile-loaded").forEach((img) => {
        const r = img.getBoundingClientRect();
        g.drawImage(img, r.left - rc.left, CAB + r.top - rc.top, r.width, r.height);
      });
      if (mostrarCalor) {
        cont.querySelectorAll("canvas.leaflet-heatmap-layer").forEach((c) => {
          const r = c.getBoundingClientRect();
          g.drawImage(c, r.left - rc.left, CAB + r.top - rc.top, r.width, r.height);
        });
      } else {
        puntos.forEach((p) => {
          const pt = map.latLngToContainerPoint([p.lat, p.lng]);
          if (pt.x < -10 || pt.y < -10 || pt.x > W + 10 || pt.y > H + 10) return;
          g.beginPath(); g.arc(pt.x, CAB + pt.y, 8, 0, Math.PI * 2);
          g.globalAlpha = 0.85; g.fillStyle = p.color; g.fill(); g.globalAlpha = 1;
          g.setLineDash(p.punteado ? [3, 3] : []); g.lineWidth = p.punteado ? 2 : 1; g.strokeStyle = "#0e2019"; g.stroke();
        });
        g.setLineDash([]);
      }
      g.restore();
      g.strokeStyle = "#dee2e6"; g.strokeRect(0.5, CAB + 0.5, W - 1, H - 1);

      // Leyenda y créditos
      g.font = "12.5px Segoe UI, Arial, sans-serif";
      leyenda.forEach((l, i) => {
        const x = 16 + (i % 2) * (W / 2), y = CAB + H + 20 + Math.floor(i / 2) * 22;
        g.beginPath(); g.arc(x + 5, y - 4, 6, 0, Math.PI * 2); g.fillStyle = l.color; g.fill();
        g.fillStyle = "#212529"; g.fillText(`${l.label}: ${l.n}`, x + 16, y);
      });
      g.fillStyle = "#6c757d"; g.font = "11px Segoe UI, Arial, sans-serif";
      g.fillText(`${mostrarCalor ? "Mapa de calor" : capa === "pai" ? "Cada punto es la ubicación de una vivienda con personas en este filtro" : "Borde punteado = pendiente de revisita"}  ·  Mapa base © colaboradores de OpenStreetMap`, 16, CAB + H + PIE - 10);

      const blob = await new Promise((res, rej) => {
        try { cv.toBlob((b) => (b ? res(b) : rej(new Error("vacío"))), "image/png"); } catch (e) { rej(e); }
      });
      const nombre = [capa === "pai" ? "Mapa_PAI" : "Mapa_Visitas", ctx?.territorio, alcance === "general" ? "territorio" : ctx?.comunidad, desde || hasta ? `${desde || "inicio"}_a_${hasta || "hoy"}` : null]
        .filter(Boolean).join("_").replace(/[^\wÁÉÍÓÚáéíóúÑñ-]+/g, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${nombre}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      alert("No se pudo generar la imagen del mapa. Revisa la conexión (el mapa base debe estar cargado) e intenta de nuevo.");
    } finally {
      setDescargando(false);
    }
  };

  const BTN = (activo) => ({
    border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    background: activo ? "#16342a" : "transparent", color: activo ? "#fff" : "#374151",
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: 8, flexWrap: "wrap" }}>
        {/* Qué mapa ver */}
        <div style={{ display: "inline-flex", background: "#eef1e7", borderRadius: 10, padding: 3 }}>
          <button onClick={() => setCapa("visitas")} style={BTN(capa === "visitas")}>🏠 Mapa de visitas</button>
          <button onClick={() => setCapa("pai")} style={BTN(capa === "pai")}>💉 Mapa barrido PAI</button>
        </div>
        {/* Alcance */}
        <div style={{ display: "inline-flex", background: "#eef1e7", borderRadius: 10, padding: 3 }}>
          {[
            { id: "comunidad", label: `🏘 ${ctx?.comunidad || "Esta comunidad"}` },
            { id: "general",   label: `📍 Todo ${ctx?.territorio || ""}` },
          ].map((op) => (
            <button key={op.id} onClick={() => setAlcance(op.id)} style={BTN(alcance === op.id)}>{op.label}</button>
          ))}
        </div>
      </div>

      {/* Fechas + acciones */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>📅 Entre</label>
        <input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)}
          className="form-control form-control-sm" style={{ maxWidth: 150 }} aria-label="Fecha desde" />
        <span style={{ fontSize: 12, color: "#6c757d" }}>y</span>
        <input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)}
          className="form-control form-control-sm" style={{ maxWidth: 150 }} aria-label="Fecha hasta" />
        {(desde || hasta) && (
          <button onClick={() => { setDesde(""); setHasta(""); }} title="Quitar rango de fechas"
            style={{ background: "none", border: "none", color: "#b3261e", cursor: "pointer", fontSize: 14 }}>✕</button>
        )}
        <button onClick={() => { const h = fechaLocal(new Date().toISOString()); setDesde(h); setHasta(h); }}
          style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Hoy
        </button>
        {alcance === "comunidad" && (
          <SelectorJornada jornadas={jornadas} value={filtroJornada} onChange={onFiltroJornada} style={{ maxWidth: 280, fontSize: 12.5, padding: "4px 28px 4px 8px" }} />
        )}
        <span style={{ flex: 1 }} />
        <button onClick={() => setMostrarCalor((p) => !p)} style={{
          background: mostrarCalor ? "#c97b4a" : "#fff", color: mostrarCalor ? "#fff" : "#16342a",
          border: "1px solid #dee2e6", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
        }}>
          🔥 Mapa de calor
        </button>
        <button onClick={descargarMapa} disabled={descargando || cargando || !puntos.length} style={{
          background: "#16342a", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700,
          cursor: descargando || !puntos.length ? "not-allowed" : "pointer", opacity: descargando || !puntos.length ? 0.6 : 1,
        }}>
          {descargando ? "Generando…" : "⬇ Descargar mapa"}
        </button>
      </div>

      {/* Filtro por tipo: un clic muestra solo ese grupo en el mapa */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "#6c757d", fontWeight: 600, marginRight: 2 }}>Mostrar:</span>
        {filtrosCapa.map((f) => {
          const activo = filtroActivo.id === f.id;
          return (
            <button key={f.id} onClick={() => setFiltro(f.id)} aria-pressed={activo}
              title={f.id === "todos" ? "Ver todas las viviendas" : `Ver solo: ${f.label}`}
              style={{
                border: `1.5px solid ${activo ? (f.color || "#16342a") : "#dee2e6"}`, borderRadius: 99, padding: "4px 11px",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                background: activo ? (f.color || "#16342a") : "#fff", color: activo ? "#fff" : "#374151",
                opacity: !activo && f.n === 0 ? 0.5 : 1,
              }}>
              {f.label} <span style={{ fontWeight: 800, marginLeft: 2, opacity: 0.9 }}>{f.n}</span>
            </button>
          );
        })}
      </div>

      {soloEsteRegistro && codigoFoco && (
        <div className="d-flex justify-content-between align-items-center p-2 px-3 rounded mb-3" style={{ background: "#faf0dc", border: "1px solid rgba(183,121,31,.3)" }}>
          <span style={{ fontSize: 12.5, color: "#854F0B" }}>
            📍 Mostrando solo <strong style={{ fontFamily: "monospace" }}>{codigoFoco}</strong>
          </span>
          <button onClick={() => setSoloEsteRegistro(false)} style={{
            background: "#fff", border: "1px solid #dee2e6", borderRadius: 6, padding: "4px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer",
          }}>
            Ver todos los puntos
          </button>
        </div>
      )}

      <div style={{ fontSize: 13, color: "#6c757d", marginBottom: 8 }}>
        {cargando ? "Cargando..." : `${resumen} · ${textoRango}`}
      </div>

      <div ref={mapRef} style={{ height: "60vh", minHeight: 420, borderRadius: 12, overflow: "hidden", border: "1px solid #e9ecef" }} />

      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12, color: "#6c757d", flexWrap: "wrap" }}>
        {leyenda.map(({ label, color, n }) => (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block" }} />
            {label} <b style={{ color: "#212529" }}>{n}</b>
          </span>
        ))}
        {capa !== "pai" && <span>· El borde punteado indica revisita pendiente</span>}
      </div>
    </div>
  );
}
